import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Share2, Users, Calendar, Check } from 'lucide-react';
import adminService from '../services/adminService';

function ShareModal({ isOpen, onClose, onSubmit, file }) {
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    shared_with_user_id: '',
    can_download: true,
    can_reshare: false,
    expires_at: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      // Reset form
      setFormData({
        shared_with_user_id: '',
        can_download: true,
        can_reshare: false,
        expires_at: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const allUsers = await adminService.getAllUsers();
      // Filter out the current user (file owner)
      setUsers(allUsers);
    } catch (err) {
      setErrors({ users: 'Failed to load users' });
    } finally {
      setLoadingUsers(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shared_with_user_id) {
      newErrors.user = 'Please select a user to share with';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSubmit = {
        shared_with_user_id: parseInt(formData.shared_with_user_id),
        can_download: formData.can_download,
        can_reshare: formData.can_reshare,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };
      
      await onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 rounded-full p-2">
              <Share2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Share File</h2>
              <p className="text-sm text-gray-500">{file?.original_filename}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{errors.submit}</span>
            </div>
          )}

          {errors.users && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
              {errors.users}
            </div>
          )}

          {/* Select User */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Share With
              </div>
            </label>
            <select
              value={formData.shared_with_user_id}
              onChange={(e) => setFormData({ ...formData, shared_with_user_id: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.user ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={loadingUsers}
            >
              <option value="">Select a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} ({user.email}) - {user.role}
                </option>
              ))}
            </select>
            {errors.user && (
              <p className="mt-1 text-sm text-red-600">{errors.user}</p>
            )}
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Expiration Date (Optional)
              </div>
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank for permanent access
            </p>
          </div>

          {/* Options */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="can_download"
                checked={formData.can_download}
                onChange={(e) => setFormData({ ...formData, can_download: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="can_download" className="ml-2 block text-sm text-gray-700">
                Allow download
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="can_reshare"
                checked={formData.can_reshare}
                onChange={(e) => setFormData({ ...formData, can_reshare: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="can_reshare" className="ml-2 block text-sm text-gray-700">
                Allow re-sharing with others
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingUsers}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                'Sharing...'
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Share File
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ShareModal;
